import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useParams, useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Divider, List, Menu, MenuItem, Typography } from '@mui/material';
import { Edit, MoreVert, VisibilityOffOutlined } from '@mui/icons-material';

// project imports
import ProjectNavItem from '../project-nav-item';
import UpdateProjectDialog from '../update-project-dialog';
import DeleteProjectPopup from '../delete-project-dialog';
import { useDispatch, useSelector } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { closeBackdrop, openBackdrop } from 'store/slices/backdrop';
import { getProjects, updateProject } from 'store/slices/project';

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //
const userActionItemContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '6px',
    padding: '16px 24px',
    boxShadow: '0px 0px 4px 0px #7A7A7A40',
    marginBottom: '16px'
};

const userActionItemContentStyle = {
    display: 'flex',
    alignItems: 'center'
};

const ProjectNavGroup = ({ item }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { workspaceId } = useParams();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [projectDeleteWarning, setProjectDeleteWarning] = useState(false);
    const [selected, setSelected] = useState(null);

    const handleProjectEdit = (projectId) => {
        setSelected(projectId);
        setDialogOpen(true);
    }

    const handleProjectArchive = (projectId) => {
        setSelected(projectId);
        setProjectDeleteWarning(true);
    }

    const handleDialogClose = () => {
        setSelected(null);
        setDialogOpen(false);
    }

    const handleDeleteWarningClose = () => {
        setSelected(null);
        setProjectDeleteWarning(false);
    }

    const handleProjectUpdate = (data) => {
        handleDialogClose()
        dispatch(
            updateProject(data, () => {
                dispatch(
                    getProjects({
                        workspaceId: workspaceId,
                    })
                );
                dispatch
                    (openSnackbar({
                        open: true,
                        anchorOrigin: { vertical: 'top', horizontal: 'right' },
                        message: <FormattedMessage id="project-snackbar-update-project" />,
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        }
                    })
                );
            })
        );
    }

    const handleProjectDelete = (data) => {
        dispatch(
            updateProject(data, () => {
                dispatch(
                    getProjects({
                        workspaceId: workspaceId,
                    })
                );
                dispatch
                    (openSnackbar({
                        open: true,
                        anchorOrigin: { vertical: 'top', horizontal: 'right' },
                        message: <FormattedMessage id="project-snackbar-delete-project" />,
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        }
                    })
                );
                handleDeleteWarningClose();
            })
        );
    }

    // menu list collapse & items
    const items = item.children?.map((menu) => (
        <ProjectNavItem
            projectInfo={menu}
            handleProjectEdit={handleProjectEdit}
            handleProjectArchive={handleProjectArchive}
        />
    ));

    return (
        <>
            <List
                subheader={
                    item.title && (
                        <Typography
                            variant="caption"
                            sx={{
                                ...theme.typography.menuCaption,
                                color: '#717D96',
                                fontSize: '0.6875rem',
                                fontWeight: 500
                            }}
                            display="block"
                            gutterBottom
                        >
                            {item.title}
                            {item.caption && (
                                <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                    {item.caption}
                                </Typography>
                            )}
                        </Typography>
                    )
                }
                sx={{
                    '& .MuiList-root:after': {
                        width: 0
                    }
                }}
            >
                {items}
            </List>
            <UpdateProjectDialog
                id={selected}
                open={dialogOpen}
                handleDialogClose={handleDialogClose}
                handleProjectUpdate={handleProjectUpdate}
            />
            <DeleteProjectPopup
                id={selected}
                open={projectDeleteWarning}
                handleClosePopup={handleDeleteWarningClose}
                handleDeleteProject={handleProjectDelete}
            />
        </>
    );
};

ProjectNavGroup.propTypes = {
    item: PropTypes.object
};

export default ProjectNavGroup;
